import { useForm } from "react-hook-form";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Stepper,
	Step,
	StepLabel,
} from "@mui/material";
import { Button } from "@cofano/cck";
import { Language } from "imports/Utils";
import useLayout from "components/layout/useLayout";
import { useState, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { gql } from "__generated__/gql";
import { entityWithDocument, openSidebarWithPdf } from "../pdf/PdfContainer";
import StepHeader from "./letterOfProtestVariableSteps/StepHeader";
import StepOpening from "./letterOfProtestVariableSteps/StepOpening";
import StepSituation from "./letterOfProtestVariableSteps/StepSituation";
import StepQuantity from "./letterOfProtestVariableSteps/StepQuantity";
import StepQuality from "./letterOfProtestVariableSteps/StepQuality";
import StepTime from "./letterOfProtestVariableSteps/StepTime";
import StepOthers from "./letterOfProtestVariableSteps/StepOthers";
import StepFooter from "./letterOfProtestVariableSteps/StepFooter";

import "./styling/styling.scss";

const LetterOfProtestDialog = ({ open, onClose, cargoActionId }) => {
	const templateKey = "LETTER_OF_PROTEST_TEMPLATE";

	const layout = useLayout();
	const [ loadingLoP, setLoadingLoP ] = useState(false);
	const [ activeStep, setActiveStep ] = useState(0);

	const handleNext = () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const { control, handleSubmit, watch, reset, setValue } = useForm({
		defaultValues: {
			cargoActionId: cargoActionId,
			templateKey: templateKey,
			protestVariables: {
				contaminants: [ ...Array(5) ].map(_ => ({ item: null, measuredPpm: null, specPpm: null })),
			},
		},
	});

	const values = watch();

	const steps = [  {
		title: "LETTER_OF_PROTEST_HEADER",
		content: <StepHeader control={control} />,
	}, {
		title: "LETTER_OF_PROTEST_OPENING",
		content: <StepOpening control={control} />,
	}, {
		title: "LETTER_OF_PROTEST_SITUATION_HEADER",
		content: <StepSituation control={control} />,
	}, {
		title: "LETTER_OF_PROTEST_QUANTITY",
		content: <StepQuantity control={control} values={values} setValue={setValue} />,
	}, {
		title: "LETTER_OF_PROTEST_QUALITY",
		content: <StepQuality control={control} values={values} setValue={setValue} />,
	}, {
		title: "LETTER_OF_PROTEST_TIME_HEADER",
		content: <StepTime control={control} />,
	}, {
		title: "LETTER_OF_PROTEST_OTHERS",
		content: <StepOthers control={control} />,
	}, {
		title: "LETTER_OF_PROTEST_FOOTER",
		content: <StepFooter control={control} />,
	} ];

	useEffect(() => {
		if (cargoActionId) {
			getTemplateOverride({
				variables: {
					cargoActionId: cargoActionId,
					templateKey: templateKey,
				},
			});
		}
	}, [ cargoActionId ]);

	const [ getTemplateOverride ] = useLazyQuery(TEMPLATE_OVERRIDE_QUERY, {
		onCompleted: response => {
			reset({
				...values,
				id: response.templateOverrideByActionAndTemplateKey?.id,
				cargoActionId: response.templateOverrideByActionAndTemplateKey?.cargoAction?.id,
				protestVariables: {
					...response.templateOverrideByActionAndTemplateKey?.variables,
				},
			});
		},
	});

	const [ saveTemplateOverride ] = useMutation(TEMPLATE_OVERRIDE_MUTATION);

	const save = (data) => {
		setLoadingLoP(true);
		saveTemplateOverride({
			variables: {
				input: {
					...data,
				},
			},
		}).then(result => {
			openLetterOfProtest(result.data.saveTemplateOverride.cargoAction.id);
			reset({
				...values,
				id: result.data.saveTemplateOverride.id,
			});
			setActiveStep(0);
			setLoadingLoP(false);
		}).catch(() => {
			setLoadingLoP(false);
		});
	};

	const handleOnClose = () => {
		onClose();
		reset();
	};

	const openLetterOfProtest = (id) => {
		onClose();
		openSidebarWithPdf(entityWithDocument.CARGO_ACTION_LOP, id, layout);
		setLoadingLoP(false);
	};

	return (
		<Dialog
			open={open}
			onClose={handleOnClose}
			maxWidth="lg"
			fullWidth
		>
			<DialogTitle>
				{Language("LETTER_OF_PROTEST")}
			</DialogTitle>

			<form>
				<DialogContent style={{ height: 600, overflowY: "auto", padding: 0 }} dividers>
					<div style={{ borderBottom: "1px solid #DDD", padding: 16 }}>
						<Stepper activeStep={activeStep} alternativeLabel>
							{steps.map((step) => (
								<Step key={step.title}>
									<StepLabel>{Language(step.title)}</StepLabel>
								</Step>
							))}
						</Stepper>
					</div>

					<div style={{ padding: 24 }}>
						<Grid spacing={3} className="letter-of-protest-dialog-content" container>
							{steps[activeStep].content}
						</Grid>
					</div>
				</DialogContent>

				<DialogActions>
					<Button onClick={() => handleOnClose()} size="small" variant="text">
						{Language("CANCEL")}
					</Button>

					<Button disabled={activeStep === 0}
						onClick={handleBack}
						size="small"
						variant="text"
					>
						{Language("PREVIOUS_PAGE")}
					</Button>

					<Button disabled={activeStep === (steps.length - 1)}
						onClick={handleNext}
						loading={loadingLoP}
						size="small"
						variant="text"
					>
						{Language("NEXT_PAGE")}
					</Button>

					<Button onClick={handleSubmit(save)}
						loading={loadingLoP}
						size="small"
						variant="text"
					>
						{Language("SAVE")}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

const TEMPLATE_OVERRIDE_MUTATION = gql(`#graphql
	mutation saveTemplateOverride($input: TemplateOverrideInput!) {
		saveTemplateOverride(input: $input) {
			id
			templateKey
			cargoAction {
				id
			}
		}
	}
`);

const TEMPLATE_OVERRIDE_QUERY = gql(`#graphql
	query templateOverrideByActionAndTemplateKey($cargoActionId:Long!, $templateKey:String!) {
		templateOverrideByActionAndTemplateKey(cargoActionId: $cargoActionId, templateKey: $templateKey){
			id
			templateKey
			cargoAction {
				id
			}
			variables {
				... on ProtestLetterVariables{
					#General
					callType
					#Header
					subject
					productName
					locationDescription
					date
					reference
					#Opening
					operator
					shipName
					voyageNumber
					#Situation
					situationBefore
					situationDuring
					situationAfter
					#Quantity
					quantityApplicable
					shipFigureDifferenceApplicable
					shipFiguresMt
					shipFiguresPercentage
					blFigureDifferenceApplicable
					blFiguresMt
					blFiguresPercentage
					generalDifferenceApplicable
					generalWeightApplicable
					generalVolumeApplicable
					generalProvisionalApplicable
					generalMeasuredApplicable
					generalOutturnWeightApplicable
					generalVolume15Applicable
					generalMt
					generalPercentage
					#Quality
					qualityApplicable
					freeWaterPresenceApplicable
					freeWaterCbm
					shoreTankApplicable
					shipsTankApplicable
					contaminants {
						item
						measuredPpm
						specPpm
					}
					qualityChangeApplicable
					# Time
					missingDocumentsApplicable
					missingDocuments
					resamplingApplicable
					dueToIncomplete
					dueToIncorrect
					dueToOutOfSpec
					failurePresentBargeApplicable
					terminalNotReadyApplicable
					terminalNotReadyReceiveDeliver
					terminalNotReadyUntilTime
					terminalNotReadyUntilDate
					tenderedNoticeApplicable
					wasReadyTo
					wasReadyToLoadDischarge
					wasReadyToAtTime
					wasReadyToAtDate
					slowRateApplicable
					slowRateShipApplicable
					slowRateShoreApplicable
					slowRateRequestApplicable
					slowRateRequest
					maximumTransferRate
					avgDischargeRate
					totalDischargeTime
					operationDelaysApplicable
					operationDelaysDueToShip
					operationDelaysDueToShore
					operationDelayNotes

					# Others
					othersNotes

					# Footer
					footerIssuedByName
					footerIssuedBySignature
					footerIssuedByPosition
					footerIssuedByPlace
					footerIssuedByDate
				}
			}
		}
	}
`);

export default LetterOfProtestDialog;
